import {
  INBOUND_MEDIA_KINDS,
  INBOUND_MEDIA_MATCH_TYPES,
  INBOUND_MEDIA_MAX_SHARE_INDEX,
  INBOUND_MEDIA_METHODS,
  INBOUND_MEDIA_SOURCES,
  INBOUND_MEDIA_STATUSES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { paginated, paginationInputSchema } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * The screen's tabs: what a person works (`unmatched`), what the automatic pass
 * still holds (`in_flight`), what is matched, and what was taken out of the
 * queue. Archived media are only in `archived`.
 */
export const INBOUND_MEDIA_TABS = ['unmatched', 'in_flight', 'matched', 'archived'] as const;
export type InboundMediaTab = (typeof INBOUND_MEDIA_TABS)[number];

/**
 * A medium as a screen shows it. What the server decides is here too: whether
 * its file can be previewed — scanned clean and not yet deleted by retention —
 * and whether it may be matched or archived now, so the screen never infers
 * either from a status.
 */
export const inboundMediaSchema = z.object({
  id: z.uuid(),
  source: z.enum(INBOUND_MEDIA_SOURCES),
  sender: z.string().nullable(),
  senderUserId: z.uuid().nullable(),
  mediaKind: z.enum(INBOUND_MEDIA_KINDS).nullable(),
  contentType: z.string().nullable(),
  byteSize: z.number().int().nullable(),
  status: z.enum(INBOUND_MEDIA_STATUSES),
  method: z.enum(INBOUND_MEDIA_METHODS).nullable(),
  matchType: z.enum(INBOUND_MEDIA_MATCH_TYPES).nullable(),
  framesProcessed: z.number().int(),
  ocrFramesSent: z.number().int(),
  processingMs: z.number().int().nullable(),
  readReferenceCode: z.string().nullable(),
  donationId: z.uuid().nullable(),
  donationReferenceCode: z.string().nullable(),
  shareIndex: z.number().int().nullable(),
  matchedAt: z.string().nullable(),
  matchedByName: z.string().nullable(),
  failureReason: z.string().nullable(),
  note: z.string().nullable(),
  isArchived: z.boolean(),
  receivedAt: z.string(),
  scanStatus: z.enum(['pending', 'clean', 'infected', 'failed']).nullable(),
  mediaDeletedAt: z.string().nullable(),
  offer: z.object({
    preview: z.boolean(),
    match: z.boolean(),
    archive: z.boolean(),
  }),
});

export type InboundMedia = z.infer<typeof inboundMediaSchema>;

export const inboundMediaListInputSchema = paginationInputSchema.extend({
  tab: z.enum(INBOUND_MEDIA_TABS).default('unmatched'),
  status: z.enum(INBOUND_MEDIA_STATUSES).optional(),
  source: z.enum(INBOUND_MEDIA_SOURCES).optional(),
  donationId: z.uuid().optional(),
  /** A prefix of the code the automatic pass read, in any case, with or without dashes. */
  search: z.string().trim().max(32).optional(),
  receivedFrom: z.iso.date().optional(),
  receivedTo: z.iso.date().optional(),
});

export type InboundMediaListInput = z.infer<typeof inboundMediaListInputSchema>;

export const inboundMediaMatchInputSchema = z.object({
  id: z.uuid(),
  /** The donation's reference code as a person reads it — never an id, which is refused. */
  referenceCode: z.string().trim().min(1).max(32),
  shareIndex: z.number().int().min(1).max(INBOUND_MEDIA_MAX_SHARE_INDEX).optional(),
});

export type InboundMediaMatchInput = z.infer<typeof inboundMediaMatchInputSchema>;

export const inboundMediaPreviewSchema = z.object({
  url: z.string(),
  contentType: z.string(),
  expiresInSeconds: z.number().int(),
});

export type InboundMediaPreview = z.infer<typeof inboundMediaPreviewSchema>;

/**
 * Inbound media — docs/plans/05-finance-reconciliation.md §5.9. A medium that
 * names no donation belongs to nobody's scope and is read with global scope
 * only; a matched one is read through its donation's scope. Nothing here moves
 * a donation. The Telegram webhook that creates media is not an oRPC route: it is
 * authenticated by its secret header, not by a session.
 */
export const inboundMediaContract = {
  list: oc
    .route({ method: 'GET', path: '/inbound-media' })
    .errors(guarded)
    .input(inboundMediaListInputSchema)
    .output(paginated(inboundMediaSchema)),

  get: oc
    .route({ method: 'GET', path: '/inbound-media/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(inboundMediaSchema),

  /** A 60-second link to the scanned file; every call writes an audit row, whether or not the link is used. */
  preview: oc
    .route({ method: 'GET', path: '/inbound-media/{id}/preview' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(inboundMediaPreviewSchema),

  /** A person's match, by the donation's reference code; audited, and moving no donation. */
  match: oc
    .route({ method: 'POST', path: '/inbound-media/{id}/match' })
    .errors(guarded)
    .input(inboundMediaMatchInputSchema)
    .output(inboundMediaSchema),

  /** Takes an unmatched medium out of the queue; its file is still deleted on its own retention. */
  archive: oc
    .route({ method: 'POST', path: '/inbound-media/{id}/archive' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: z.string().trim().min(1).max(500) }))
    .output(inboundMediaSchema),
};
